import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SortingOption } from '@sorare/core/src/__generated__/globalTypes';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { Auction } from 'components/auction/Auction';

import type {
  AllTokenAuctionsQuery,
  AllTokenAuctionsQueryVariables,
} from './__generated__/index.graphql';
import { DisplayItems } from '../../common/DisplayItems';
import { DefaultSortType } from '../../common/types';

const ALL_TOKEN_AUCTIONS_QUERY = gql`
  query AllTokenAuctionsQuery(
    $cursor: String
    $sortByEndDate: SortingOption
    $sport: [Sport!]
  ) {
    currentUser {
      slug
      involvedTokenAuctions(
        first: 10
        after: $cursor
        sortByEndDate: $sortByEndDate
        sport: $sport
      ) {
        nodes {
          id
          ...Auction_auction
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Auction.fragments.auction}
` as TypedDocumentNode<AllTokenAuctionsQuery, AllTokenAuctionsQueryVariables>;

export const AllAuctions = ({
  sortType,
  sport,
}: {
  sortType: DefaultSortType;
  sport: Sport[];
}) => {
  const sortByEndDate =
    sortType === DefaultSortType.LATEST
      ? SortingOption.DESC
      : SortingOption.ASC;

  const { loading, data, loadMore, refetch } = usePaginatedQuery(
    ALL_TOKEN_AUCTIONS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      connection: 'TokenAuctionConnection',
      variables: {
        sortByEndDate,
        sport,
      },
    }
  );

  const { items, count, cursor, hasMore } = extractConnectionData(
    data?.currentUser?.involvedTokenAuctions,
    p => <Auction key={p.id} auction={p} />
  );

  const displayLoading = !items && loading;

  return (
    <PullToRefetch refetch={refetch}>
      <DisplayItems
        displayLoading={displayLoading}
        count={count}
        items={items}
        loadMoreSection={{
          hasMore,
          loading,
          loadMore: () => {
            loadMore(false, {
              cursor,
              sortByEndDate,
              sport,
            });
          },
        }}
      />
    </PullToRefetch>
  );
};
